/**
 * Cache manager for downloading and storing data for offline use via API
 * Following OBY api-conventions.json: consistent error handling, proper status codes
 */
import {
  cacheCustomers,
  cacheProducts,
  cacheServices,
  getCachedCustomers,
  getCachedProducts,
  getCachedServices,
  getSyncMetadata,
  setSyncMetadata,
} from "./db";
import type { CachedCustomer, CachedProduct, CachedService } from "./types";

const CACHE_STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Check if cached data is stale (older than threshold)
 */
export async function isCacheStale(key: "customers" | "products" | "services"): Promise<boolean> {
  try {
    const metadata = await getSyncMetadata(key);
    if (!metadata) {
      return true; // No metadata means never cached
    }

    const lastSynced = new Date(metadata.last_synced_at).getTime();
    const now = Date.now();
    const isStale = now - lastSynced > CACHE_STALE_THRESHOLD_MS;

    return isStale;
  } catch (error) {
    console.error("[Cache Manager] Error checking cache staleness:", error);
    return true; // Assume stale on error
  }
}

/**
 * Download and cache customers from API
 */
export async function downloadCustomers(): Promise<{
  success: boolean;
  count: number;
  error?: string;
}> {
  try {
    console.log("[Cache Manager] Downloading customers...");

    const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";
    const response = await fetch(`${apiUrl}/api/v1/customers?limit=1000`);

    if (!response.ok) {
      const errorMessage = `Failed to fetch customers: ${response.statusText}`;
      console.error("[Cache Manager] Error fetching customers:", errorMessage);
      return { success: false, count: 0, error: errorMessage };
    }

    const result = await response.json();
    const data = result.data || [];

    if (!data || data.length === 0) {
      console.log("[Cache Manager] No customers to cache");
      await setSyncMetadata({
        key: "customers",
        last_synced_at: new Date().toISOString(),
        record_count: 0,
        status: "success",
      });
      return { success: true, count: 0 };
    }

    // Transform to cached format with unique customers by name/email
    const uniqueCustomers = new Map<string, CachedCustomer>();

    for (const project of data) {
      const key = `${project.customer_name}-${project.customer_email || ""}`;
      if (!uniqueCustomers.has(key)) {
        uniqueCustomers.set(key, {
          id: project.id,
          name: project.customer_name,
          email: project.customer_email || undefined,
          phone: project.customer_phone || undefined,
          address: project.customer_address || undefined,
          created_at: project.created_at,
          cached_at: new Date().toISOString(),
        });
      }
    }

    const customersToCache = Array.from(uniqueCustomers.values());
    await cacheCustomers(customersToCache);

    await setSyncMetadata({
      key: "customers",
      last_synced_at: new Date().toISOString(),
      record_count: customersToCache.length,
      status: "success",
    });

    console.log(`[Cache Manager] Cached ${customersToCache.length} customers`);
    return { success: true, count: customersToCache.length };
  } catch (error) {
    console.error("[Cache Manager] Error downloading customers:", error);
    const errorMessage = error instanceof Error ? error.message : "Unknown error";

    await setSyncMetadata({
      key: "customers",
      last_synced_at: new Date().toISOString(),
      record_count: 0,
      status: "failed",
      error: errorMessage,
    });

    return { success: false, count: 0, error: errorMessage };
  }
}

/**
 * Download and cache products from API
 */
export async function downloadProducts(): Promise<{
  success: boolean;
  count: number;
  error?: string;
}> {
  try {
    console.log("[Cache Manager] Downloading products...");

    const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";
    const response = await fetch(`${apiUrl}/api/v1/products`);

    if (!response.ok) {
      const errorMessage = `Failed to fetch products: ${response.statusText}`;
      console.error("[Cache Manager] Error fetching products:", errorMessage);
      return { success: false, count: 0, error: errorMessage };
    }

    const result = await response.json();
    const data = result.data || [];

    if (!data || data.length === 0) {
      console.log("[Cache Manager] No products to cache");
      await setSyncMetadata({
        key: "products",
        last_synced_at: new Date().toISOString(),
        record_count: 0,
        status: "success",
      });
      return { success: true, count: 0 };
    }

    const productsToCache: CachedProduct[] = data.map((product) => ({
      id: product.id,
      name: product.name,
      category: product.category,
      price: product.price,
      description: product.description || undefined,
      image_url: product.image_url || undefined,
      cached_at: new Date().toISOString(),
    }));

    await cacheProducts(productsToCache);

    await setSyncMetadata({
      key: "products",
      last_synced_at: new Date().toISOString(),
      record_count: productsToCache.length,
      status: "success",
    });

    console.log(`[Cache Manager] Cached ${productsToCache.length} products`);
    return { success: true, count: productsToCache.length };
  } catch (error) {
    console.error("[Cache Manager] Error downloading products:", error);
    const errorMessage = error instanceof Error ? error.message : "Unknown error";

    await setSyncMetadata({
      key: "products",
      last_synced_at: new Date().toISOString(),
      record_count: 0,
      status: "failed",
      error: errorMessage,
    });

    return { success: false, count: 0, error: errorMessage };
  }
}

/**
 * Download and cache services from API
 */
export async function downloadServices(): Promise<{
  success: boolean;
  count: number;
  error?: string;
}> {
  try {
    console.log("[Cache Manager] Downloading services...");

    const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";
    const response = await fetch(`${apiUrl}/api/v1/checklists/services`);

    if (!response.ok) {
      const errorMessage = `Failed to fetch services: ${response.statusText}`;
      console.error("[Cache Manager] Error fetching services:", errorMessage);
      return { success: false, count: 0, error: errorMessage };
    }

    const result = await response.json();
    const data = result.data || [];

    if (!data || data.length === 0) {
      console.log("[Cache Manager] No services to cache");
      await setSyncMetadata({
        key: "services",
        last_synced_at: new Date().toISOString(),
        record_count: 0,
        status: "success",
      });
      return { success: true, count: 0 };
    }

    const servicesToCache: CachedService[] = data.map((service) => ({
      id: service.id,
      name: service.name,
      description: service.description || undefined,
      base_price: service.base_price,
      pricing_type: service.pricing_type || undefined,
      cached_at: new Date().toISOString(),
    }));

    await cacheServices(servicesToCache);

    await setSyncMetadata({
      key: "services",
      last_synced_at: new Date().toISOString(),
      record_count: servicesToCache.length,
      status: "success",
    });

    console.log(`[Cache Manager] Cached ${servicesToCache.length} services`);
    return { success: true, count: servicesToCache.length };
  } catch (error) {
    console.error("[Cache Manager] Error downloading services:", error);
    const errorMessage = error instanceof Error ? error.message : "Unknown error";

    await setSyncMetadata({
      key: "services",
      last_synced_at: new Date().toISOString(),
      record_count: 0,
      status: "failed",
      error: errorMessage,
    });

    return { success: false, count: 0, error: errorMessage };
  }
}

/**
 * Download all data for offline use (customers, products, services)
 */
export async function downloadAllData(): Promise<{
  success: boolean;
  results: {
    customers: { success: boolean; count: number; error?: string };
    products: { success: boolean; count: number; error?: string };
    services: { success: boolean; count: number; error?: string };
  };
}> {
  console.log("[Cache Manager] Downloading all data for offline use...");

  const [customersResult, productsResult, servicesResult] = await Promise.all([
    downloadCustomers(),
    downloadProducts(),
    downloadServices(),
  ]);

  const allSuccess = customersResult.success && productsResult.success && servicesResult.success;

  return {
    success: allSuccess,
    results: {
      customers: customersResult,
      products: productsResult,
      services: servicesResult,
    },
  };
}

/**
 * Get cached data with fallback to API if online
 */
export async function getCachedOrFetchCustomers(forceRefresh = false): Promise<CachedCustomer[]> {
  try {
    const isStale = await isCacheStale("customers");

    if (forceRefresh || isStale) {
      console.log("[Cache Manager] Cache stale or refresh forced, downloading customers...");
      const result = await downloadCustomers();
      if (!result.success) {
        console.warn("[Cache Manager] Download failed, using cached data");
        return await getCachedCustomers();
      }
    }

    return await getCachedCustomers();
  } catch (error) {
    console.error("[Cache Manager] Error getting customers:", error);
    return [];
  }
}

export async function getCachedOrFetchProducts(forceRefresh = false): Promise<CachedProduct[]> {
  try {
    const isStale = await isCacheStale("products");

    if (forceRefresh || isStale) {
      console.log("[Cache Manager] Cache stale or refresh forced, downloading products...");
      const result = await downloadProducts();
      if (!result.success) {
        console.warn("[Cache Manager] Download failed, using cached data");
        return await getCachedProducts();
      }
    }

    return await getCachedProducts();
  } catch (error) {
    console.error("[Cache Manager] Error getting products:", error);
    return [];
  }
}

export async function getCachedOrFetchServices(forceRefresh = false): Promise<CachedService[]> {
  try {
    const isStale = await isCacheStale("services");

    if (forceRefresh || isStale) {
      console.log("[Cache Manager] Cache stale or refresh forced, downloading services...");
      const result = await downloadServices();
      if (!result.success) {
        console.warn("[Cache Manager] Download failed, using cached data");
        return await getCachedServices();
      }
    }

    return await getCachedServices();
  } catch (error) {
    console.error("[Cache Manager] Error getting services:", error);
    return [];
  }
}
